import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../utils/axiosInstance";

function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "— CAD";
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "CAD" }).format(n);
  } catch (_) {
    return `${n.toFixed(2)} CAD`;
  }
}

export default function BalanceCard({ className = "" }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [wallet, setWallet] = useState({
    balanceCad: "0.00",
    heldCad: "0.00",
    availableCad: "0.00",
  });

  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get("/api/wallet");
      const data = res.data || {};
      const w = data.wallet || {};

      setWallet({
        balanceCad: String(w.balanceCad ?? "0.00"),
        heldCad: String(w.heldCad ?? "0.00"),
        availableCad: String(w.availableCad ?? "0.00"),
      });

      setLastUpdated(w.updatedAt || data.updatedAt || new Date().toISOString());
    } catch (err) {
      console.error("BalanceCard fetchWallet error:", err?.response?.data || err?.message || err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Impossible de charger votre solde.";

      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  const mainBalance = useMemo(() => formatMoney(wallet.balanceCad), [wallet.balanceCad]);

  const subLine = useMemo(() => {
    return `Held: ${formatMoney(wallet.heldCad)} • Avail: ${formatMoney(wallet.availableCad)}`;
  }, [wallet.heldCad, wallet.availableCad]);

  const formattedTime = useMemo(() => {
    if (!lastUpdated) return "Non spécifié";
    try {
      return new Date(lastUpdated).toLocaleString("fr-FR");
    } catch (_) {
      return "Non spécifié";
    }
  }, [lastUpdated]);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-6 shadow-sm
                 border border-black/5 dark:border-white/10
                 bg-white dark:bg-slate-900 ${className}`}
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 -bottom-24 h-48 w-48 rounded-full bg-amber-400/10 blur-3xl" />

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-slate-500 dark:text-slate-400">Votre solde (CAD)</div>

          <div className="mt-2">
            {loading ? (
              <div className="h-9 w-56 rounded-xl bg-black/10 dark:bg-white/10 animate-pulse" />
            ) : (
              <div className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                {mainBalance}
              </div>
            )}
          </div>

          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {loading ? (
              <div className="h-3 w-44 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
            ) : (
              <>
                {subLine}
                <span className="block mt-1">Dernière mise à jour: {formattedTime}</span>
              </>
            )}
          </div>

          {!loading && error && (
            <div className="mt-3 rounded-2xl border border-rose-500/20 bg-rose-500/10 p-3">
              <div className="text-sm text-rose-700 dark:text-rose-200">{error}</div>
              <button
                type="button"
                onClick={fetchWallet}
                className="mt-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline"
              >
                Réessayer
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="inline-flex items-center justify-center rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-3 py-2">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">CAD</span>
          </div>

          <button
            type="button"
            onClick={fetchWallet}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl
                       border border-black/10 dark:border-white/10
                       bg-white/60 dark:bg-white/5
                       px-3 py-2 text-xs font-medium text-slate-900 dark:text-white
                       hover:bg-black/5 dark:hover:bg-white/10 transition
                       disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualiser le solde"
          >
            <span aria-hidden>↻</span>
            {loading ? "Chargement" : "Actualiser"}
          </button>
        </div>
      </div>
    </div>
  );
}
